// Imports
import React, { useEffect, useState, useCallback, useMemo, useRef, JSX } from "react";
import styled from "styled-components";

// Components
import CustomInput from "../../form/CustomInput";
import FileUpload from "../../form/ProfileImageUpload";
import FormTitle from "../../form/FormTitle";
import { Button } from '../../../components/form/Button';
import { useToast } from "../../toast/ToastContext";
import FormFillerSkeleton from "./FormFillerSkeleton";
import AMSSelect from "../../AMSSelect";

// Assets
import CloseIcon from "../../../assets/icons/CloseIcon";
import CalendarIcon from "../../../assets/icons/CalendarIcon";
import { useTheme } from "../../../middleware/ThemeContext";

// Services & Utils
import { CreateFormTheme } from "../../../utils/themes";
import {
    useGetFormDataQuery,
    useSubmitFormMutation,
    useAttFromToModuleMutation
} from "../../../api/formsApiSlice";
import { FieldDataTypeEnum, MetadataField, FormRow, FormFieldTest } from "../../../types/types";
import { getRegionCodeForCountryName, normalizeCallingCode, PhoneValidationOptions } from "../../../utils/phoneValidation";
import { buildPhoneDependencyMap } from "../../../utils/phoneFieldDependencies";

// Field Settings
import RequiredIcon from "../../../assets/icons/RequiredIcon";
import AddWhiteIcon from "../../../assets/icons/AddWhiteIcon";
import ChevronDownIcon from "../../../assets/icons/ChevronDownIcon";
import ExpandIcon from "../../../assets/icons/ExpandIcon";
import ContractIcon from "../../../assets/icons/ContractIcon";
import StyledToolTip from "../../StyledComponents/StyledTooltip";
import TooltipIcon from "../../../assets/icons/Tooltip";
import PlusIcon from "../../../assets/icons/PlusIcon";
import { LoadingSpinner } from "../../../features/ui/LoadingSpinner";
import { CheckboxItem } from "../../listing/ColumnIdentifier";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import { useGetOrganizationsMinimalQuery } from "../../../api/organizationApiSlice";
import CrossIcon from "../../../assets/icons/CrossIcon";
import { FormFieldControl } from "../FieldPreviews/FormFieldControl";
import { useOnClickOutside } from "../../../hooks/useOnClickOutside";

// Props Interface
interface FormFillerProps {
    onClose: () => void;
    entityId?: number | null;
    onSuccess?: () => void;
    formType: 'contact' | 'organization';
}
type OrgRoleRow = {
    organizationId: any;
    organizationName?: string;
    rolesId: any;
    isPrimary?: boolean;
};

export type FormOption = {
    label: string;
    value: string;
    id?: number | string;
};

const FormFiller: React.FC<FormFillerProps> = ({ onClose, entityId, onSuccess, formType }) => {
    const { theme } = useTheme();
    const accessKey = formType === 'contact' ? 'ADD_CONTACT_FORM' : 'ADD_ORGANIZATION_FORM';
    const [formData, setFormData] = useState<any | null>(null);
    const [metadata, setMetadata] = useState<MetadataField[]>([]);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [metadataValues, setMetadataValues] = useState<Record<string, any>>({});
    const [formFieldValues, setFormFieldValues] = useState<Record<string, any>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [metadataErrors, setMetadataErrors] = useState<Record<string, string>>({});
    const [hasUserInteracted, setHasUserInteracted] = useState<boolean[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [canSubmit, setCanSubmit] = useState(false);
    const [isFormDropdownOpen, setIsFormDropdownOpen] = useState(false);
    const [selectedForm, setSelectedForm] = useState<FormOption | null>(null);
    const [isFormExpanded, setIsFormExpanded] = useState(false);
    const prevFormIdRef = useRef<number | null>(null);
    const submitFormData = useSubmitFormMutation()[0];
    const showToast = useToast();
    const { data, isLoading, refetch: refetchFormData, isFetching: isFetchingFormData } = useGetFormDataQuery(
        { accessKey: accessKey || '' },
        { skip: !accessKey, refetchOnMountOrArgChange: true, }
    );
    const [attachFormToModule] = useAttFromToModuleMutation();

    const formSelectParams = useMemo(() => ({ statusId: 38, scope: formType }), [formType]);

    const phoneFieldDependencies = useMemo(() => {
        const allFormFields = (formData?.formPages || [])
            .flatMap((page: any) => page?.formRows || [])
            .flatMap((row: FormRow) => row?.formFields || []);

        return buildPhoneDependencyMap(allFormFields);
    }, [formData]);

    const mapRolesResponse = useCallback((json: any, page: number) => {
        const roles = json?.roles || [];
        const totalCount = json?.totalCount ?? 0;
        const perPage = json?.perPage ?? json?.pagination?.perPage ?? 10;
        const hasMore = totalCount ? page * perPage < totalCount : roles.length === perPage;

        const options = roles.map((role: any) => {
            const value = role?.id ?? role?.value ?? role;
            return {
                label: role?.name ?? role?.label ?? String(value ?? ""),
                value,
                ...role,
            };
        });

        return {
            options,
            hasMore,
            additional: { page: page + 1 },
        };
    }, []);

    const normalizeRolesValueForSelect = useCallback((rolesValue: any) => {
        if (Array.isArray(rolesValue)) {
            return rolesValue.map((item) => {
                if (item && typeof item === "object") {
                    const value = item.value ?? item.id ?? item.roleId ?? item;
                    const label = item.label ?? item.name ?? String(value ?? "");
                    return { value, label };
                }
                return { value: item, label: String(item ?? "") };
            });
        }

        if (!rolesValue) return [];

        if (rolesValue && typeof rolesValue === "object") {
            const value = (rolesValue as any).value ?? (rolesValue as any).id ?? (rolesValue as any).roleId ?? rolesValue;
            const label = (rolesValue as any).label ?? (rolesValue as any).name ?? String(value ?? "");
            return [{ value, label }];
        }

        return [{ value: rolesValue, label: String(rolesValue ?? "") }];
    }, []);

    const mapFormsResponse = useCallback((json: any, page: number) => {
        const forms = json?.forms ?? [];
        const totalCount = json?.totalCount || 0;
        const perPage = json?.perPage;
        const options = (forms as any[]).map((form: any) => {
            const label = form?.name;
            return {
                label: form?.name,
                value: String(form?.id),
                id: form?.id,
                ...form,
            };
        });
        const hasMore = totalCount
            ? page * perPage < totalCount
            : Array.isArray(forms) && forms.length === perPage;
        return {
            options,
            hasMore,
            additional: { page: page + 1 },
        };
    }, []);

    // Fetch metadata/form data
    useEffect(() => {
        if (data) {
            const incomingFormId = data.data.form?.id;
            const isNewForm = prevFormIdRef.current !== incomingFormId;
            const previousMetadataValues = isNewForm ? {} : metadataValues;

            const initialMetadataValues = data.data.metaData.reduce((acc: any, field: MetadataField) => {
                if ((field.field === 'o.name' || field.field === 'r.name') && formType === 'contact') {
                    if (!acc["organizationRoles"]) {
                        acc["organizationRoles"] =
                            previousMetadataValues.organizationRoles && previousMetadataValues.organizationRoles.length > 0
                                ? previousMetadataValues.organizationRoles
                                :
                                [
                                    {
                                        organizationId: "",
                                        rolesId: [],
                                        isPrimary: true
                                    },
                                ];
                    }
                } else {
                    acc[field.field] = previousMetadataValues[field.field] || '';
                }
                return acc;
            }, {} as Record<string, any>);

            if (isNewForm) {
                setFormFieldValues({});
                setFieldErrors({});
                setMetadataErrors({});
                setCurrentPageIndex(0);
                setHasUserInteracted(Array(data.data.form.formPages.length).fill(false));
            }

            setMetadataValues(initialMetadataValues);
            setFormData(data.data.form);
            setMetadata(data.data.metaData);
            prevFormIdRef.current = incomingFormId ?? null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const setPageInteracted = (pageIndex: number, value: boolean) => {
        setHasUserInteracted(prev => {
            const newInteractions = [...prev];
            newInteractions[pageIndex] = value;
            return newInteractions;
        });
    };
    // Handle form switching
    const handleFormSwitch = async (formId: string) => {
        try {
            await attachFormToModule({
                formId: parseInt(formId),
                accessKey: accessKey
            }).unwrap();

            await refetchFormData().unwrap();

            showToast("Success", "Form switched successfully", "success");
        } catch (err) {
            showToast("Error", "Failed to switch form", "error");
        }
    };

    const handleFormSelect = (option: FormOption | null) => {
        if (!option) {
            return;
        }
        setSelectedForm(option);
        handleFormSwitch(option.value);
    };

    // Field Handlers
    const handleInputChange = (fieldKey: number, value: any) => {
        // setPageInteracted(currentPageIndex);
        setFormFieldValues(prev => ({ ...prev, [fieldKey]: value }));
        if (fieldErrors[fieldKey]) {
            setFieldErrors(prev => ({ ...prev, [fieldKey]: "" }));
        }
    };

    const handleMetadataChange = (fieldKey: string, value: any) => {
        // setPageInteracted(currentPageIndex);
        setMetadataValues(prev => ({ ...prev, [fieldKey]: value }));
        if (metadataErrors[fieldKey]) {
            setMetadataErrors(prev => ({ ...prev, [fieldKey]: "" }));
        }
    };

    const handleOrgRoleChange = (
        rowIndex: number,
        field: "org" | "role",
        value: any
    ) => {
        setMetadataValues((prev) => {
            const roles = Array.isArray(prev.organizationRoles)
                ? [...prev.organizationRoles]
                : [{ organizationId: "", organizationName: "", rolesId: "", isPrimary: true }];
            if (!roles[rowIndex])
                roles[rowIndex] = { organizationId: "", organizationName: "", rolesId: "", isPrimary: false };

            if (field === "org") {
                const orgId = value?.id ?? value?.value ?? value;
                const orgName = value?.name ?? value?.label ?? String(orgId);
                roles[rowIndex] = {
                    ...roles[rowIndex],
                    organizationId: orgId,
                    organizationName: orgName,
                    rolesId: "",
                };
            } else {
                roles[rowIndex] = {
                    ...roles[rowIndex],
                    rolesId: value,
                };
            }

            return { ...prev, organizationRoles: roles };
        });
        const errorKey = `organizationRoles_${field}_${rowIndex}`;
        if (metadataErrors[errorKey]) {
            setMetadataErrors((prev) => {
                const next = { ...prev };
                delete next[errorKey];
                return next;
            });
        }
    };

    const handleIsPrimaryChange = (rowIndex: number) => {
        setMetadataValues((prev) => {
            const roles = Array.isArray(prev.organizationRoles) ? [...prev.organizationRoles] : [];
            const updatedRoles = roles.map((role, idx) => ({
                ...role,
                isPrimary: idx === rowIndex, // ✅ only clicked row true
            }));
            return { ...prev, organizationRoles: updatedRoles };
        });
    };

    const addOrgRoleRow = () => {
        setMetadataValues(prev => {
            const roles = Array.isArray(prev.organizationRoles) ? [...prev.organizationRoles] : [];
            const newRow = { organizationId: "", rolesId: [], isPrimary: false };
            return { ...prev, organizationRoles: [...roles, newRow] };
        });
    };

    const removeOrgRoleRow = (rowIndex: number) => {
        setMetadataValues(prev => {
            const roles = Array.isArray(prev.organizationRoles) ? [...prev.organizationRoles] : [];
            if (roles.length <= 1) return prev;

            const removedRow = roles[rowIndex];
            roles.splice(rowIndex, 1);

            // If the removed row was primary, assign primary to first remaining row
            if (removedRow?.isPrimary && roles.length > 0) {
                roles[0] = { ...roles[0], isPrimary: true };
            }

            return { ...prev, organizationRoles: roles };
        });
    };

    const handleCompoundChange = (fieldKey: number, subFieldKey: number, value: any) => {
        setFormFieldValues(prev => ({
            ...prev,
            [fieldKey]: {
                ...(prev[fieldKey] || {}),
                [subFieldKey]: value
            }
        }));
    };

    const handleFieldError = (fieldKey: number, error: string | null) => {
        setFieldErrors(prev => ({
            ...prev,
            [fieldKey]: error || ""
        }));
    };

    const validateMetadata = (): boolean => {
        const newErrors: Record<string, string> = {};
        let isValid = true;

        metadata.forEach(field => {

            if (field.isRequired) {
                const value = metadataValues[field.field];
                const label = field.label || field.field;

                if (!value) {
                    newErrors[field.field] = `${label} is required`;
                    isValid = false;
                    return;
                }

                switch (field?.dataType) {
                    case "email":
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(value)) {
                            newErrors[field.field] = "Please enter a valid email address";
                            isValid = false;
                        }
                        break;
                    case "phone":
                        const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
                        if (!phoneRegex.test(value)) {
                            newErrors[field.field] = "Please enter a valid phone number";
                            isValid = false;
                        }
                        break;
                    case "date":
                        if (isNaN(new Date(value).getTime())) {
                            newErrors[field.field] = "Please enter a valid date";
                            isValid = false;
                        }
                        break;
                }
            }
        });

        if (formType === "contact") {

            const rows = (metadataValues.organizationRoles as { organizationId: any; rolesId: any }[]) || [];

            rows.forEach((r, idx) => {

                // role required only when org chosen and r.name configured as required
                const hasRoles = Array.isArray(r.rolesId) ? r.rolesId.length > 0 : Boolean(r.rolesId);
                if (r.organizationId && !hasRoles) {
                    newErrors["organizationRoles_role_" + idx] = "Role is required for selected organization";
                    isValid = false;
                }
            });

        }

        setMetadataErrors(newErrors);
        return isValid;
    };

    const isFormValid = (): boolean => {
        return !Object.values(fieldErrors).some(Boolean) && !Object.values(metadataErrors).some(Boolean);
    };
    const isCurrentPageValid = (): boolean => {
        // Get current page field IDs
        const currentPageFieldIds = currentPage?.formRows?.flatMap((row: FormRow) =>
            row.formFields.map((field: FormFieldTest) => field?.field?.id)
        ).filter(Boolean) || [];

        // Check only current page field errors
        const currentPageFieldErrors = Object.entries(fieldErrors)
            .filter(([fieldKey]) => currentPageFieldIds.includes(parseInt(fieldKey)))
            .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

        return !Object.values(currentPageFieldErrors).some(Boolean) && !Object.values(metadataErrors).some(Boolean);
    };
    const buildPayload = () => {
        const serializeFieldValue = (rawValue: any) => {
            if (Array.isArray(rawValue)) {
                return rawValue
                    .map((item) => {
                        if (item === undefined || item === null || item === '') return null;
                        if (typeof item === 'object') {
                            return item.value ?? item.id ?? item.fieldId ?? item;
                        }
                        return item;
                    })
                    .filter((val): val is string | number => val !== null && val !== undefined && val !== '')
                    .join(',');
            }

            if (rawValue && typeof rawValue === 'object') {
                return JSON.stringify(rawValue);
            }

            return rawValue;
        };

        const fields = Object.entries(formFieldValues)
            .filter(([_, value]) => value !== undefined && value !== null && value !== '')
            .map(([fieldId, value]) => ({
                fieldId: parseInt(fieldId),
                value: serializeFieldValue(value)
            }));

        const completeMetadata = metadata.reduce((acc, field) => {
            const value = metadataValues[field.field];
            if (value !== undefined && value !== null && value !== '') {
                acc[field.key] = value;
            }
            return acc;
        }, {} as Record<string, any>);

        if (formType === "contact" && Array.isArray(metadataValues.organizationRoles)) {

            completeMetadata["organizationRoles"] = metadataValues.organizationRoles
                .filter(r => r.organizationId)
                .map(r => ({
                    organizationId: r.organizationId,
                    rolesId: Array.isArray(r.rolesId)
                        ? r.rolesId
                            .map((role: any) => {
                                if (role && typeof role === "object") {
                                    return role.value ?? role.id ?? role.roleId ?? role;
                                }
                                return role;
                            })
                            .filter((val: any) => val !== undefined && val !== null && val !== "")
                        : r.rolesId
                            ? [
                                typeof r.rolesId === "object"
                                    ? (r.rolesId as any).value ?? (r.rolesId as any).id ?? (r.rolesId as any)
                                    : r.rolesId,
                            ]
                            : [],
                    isPrimary: !!r.isPrimary,
                }));
        }

        return {
            scope: formType.toUpperCase(),
            metaData: completeMetadata,
            ...(fields.length && {
                formDetails: {
                    formId: formData.id,
                    fields
                }
            }),
            ...(entityId && { entityId })
        };
    };

    const submitForm = async () => {
        try {
            setIsSubmitting(true);
            if (!validateMetadata() || !isFormValid()) {
                showToast("Validation Error", "Please fix all form errors before submitting", "error");
                return;
            }
            const payload = buildPayload();
            await submitFormData(payload).unwrap();
            showToast("Success", "Form submitted successfully", "success");
            onSuccess?.();
            onClose();
        } catch (err: any) {
            const errorMessage =
                err?.data?.message || err?.message ||
                (err?.status === 401 ? "Session expired. Please log in again." : "Submission failed");
            showToast("Error", errorMessage, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const addOrganization = async (orgName: string) => {
        try {
            const payload = {
                metaData: {
                    name: orgName
                },
                scope: "ORGANIZATION"
            };
            const result = await submitFormData(payload).unwrap();

            const newOrgId = result?.entityId; // Select new Organization ID

            if (!newOrgId) {
                throw new Error("Organization created but ID not returned");
            }
            showToast("Success", "Organization Added successfully", "success");
            return newOrgId; // Return selected ID
        } catch (err: any) {
            const errorMessage =
                err?.data?.message || err?.message ||
                (err?.status === 401 ? "Session expired. Please log in again." : "Submission failed");
            showToast("Error", errorMessage, "error");
        } finally {
            // setIsSubmitting(false);
        }
    }

    const makeChangeHandler = (field: FormFieldTest) => {
        const fieldKey = field?.field?.id || 123;
        return (value: any, subFieldKey?: number) => {
            subFieldKey
                ? handleCompoundChange(fieldKey, subFieldKey, value)
                : handleInputChange(fieldKey, value);
        };
    };

    const renderMetadataField = (field: MetadataField) => {
        if (field.dataType === 'image') return null;
        const value = metadataValues[field.field] || '';
        const error = metadataErrors[field.field];
        const label = field.label || field.field;
        return (
            <FormGroup key={field.field}>
                <FieldLabel theme={theme} className="mb-1">
                    {label} {field.isRequired && <RequiredIcon />}
                </FieldLabel>
                <CustomInput
                    value={value}
                    error={error}
                    required={field.isRequired}
                    onChange={(val: string) => handleMetadataChange(field.field, val)}
                    type={getInputType(field.dataType)}
                    placeholder={getPlaceholder(field.dataType, label)}
                    rightIcon={field.dataType === "date" && <CalendarIcon />}
                />
            </FormGroup>
        );
    };

    const renderMultipleOrganizations = () => {
        const rows: OrgRoleRow[] =
            metadataValues?.organizationRoles ?? [{ organizationId: "", rolesId: [], isPrimary: true }];

        return (
            <div className="p-3">
                {rows.map((row: OrgRoleRow, index: number) => (
                    <div className="row gap-2" key={index}>
                        {/* Organization */}
                        <div className="col p-0">
                            <FormGroup className="mb-0">
                                <FieldLabel theme={theme} className="mb-1">Select Organization</FieldLabel>
                                <AMSSelect
                                    endpoint="/organizations/minimal"
                                    defaultOptions
                                    includeId
                                    includeImage
                                    placeholder="Select Organization"
                                    value={row.organizationId ? { id: row.organizationId, value: row.organizationId, label: (row as any).organizationName || String(row.organizationId) } : null}
                                    onChange={(option) => handleOrgRoleChange(index, "org", option)}
                                    getOptionLabel={(option: any) => option?.name ?? option?.label ?? ""}
                                    getOptionValue={(option: any) => option?.id ?? option?.value}
                                    includeAddButton
                                    addButtonText="Create Organization"
                                    onAddButtonClick={async (val: string) => {
                                        const newOrgId = await addOrganization(val);
                                        if (newOrgId) {
                                            handleOrgRoleChange(index, "org", { id: newOrgId, name: val });
                                        }
                                    }}

                                />
                                {hasUserInteracted && metadataErrors["organizationRoles_org_" + index] && (
                                    <ErrorText theme={theme}>
                                        {metadataErrors["organizationRoles_org_" + index]}
                                    </ErrorText>
                                )}
                            </FormGroup>
                        </div>

                        {/* Role (only when org selected) */}
                        {row.organizationId && (
                            <div className="col p-0">
                                <FormGroup className="mb-0">
                                    <FieldLabel theme={theme} className="mb-1">
                                        Select Roles
                                        {<RequiredIcon />}
                                    </FieldLabel>
                                    <AMSSelect
                                        endpoint="/contact-roles"
                                        defaultOptions
                                        mapResponse={mapRolesResponse}
                                        placeholder="Select Roles"
                                        isMulti
                                        value={normalizeRolesValueForSelect(row.rolesId)}
                                        onChange={(options) => handleOrgRoleChange(
                                            index,
                                            "role",
                                            Array.isArray(options) ? options : options ? [options] : []
                                        )}
                                    />
                                    {hasUserInteracted && metadataErrors["organizationRoles_role_" + index] && (
                                        <ErrorText theme={theme}>
                                            {metadataErrors["organizationRoles_role_" + index]}
                                        </ErrorText>
                                    )}
                                </FormGroup>
                            </div>
                        )}

                        {/* Add / Remove */}
                        <div className="col-1 d-flex align-items-center gap-2 p-0">
                            {index === rows.length - 1 ? (
                                <Button type='button' className="mt-4" theme={theme} $variant="customize" width="42px" onClick={addOrgRoleRow}><PlusIcon /> </Button>
                            ) : (
                                <Button type="button" className="mt-4" theme={theme} $variant="customize" width="42px" onClick={() => removeOrgRoleRow(index)}><DeleteIcon /> </Button>
                            )}
                        </div>
                        <div className="col-12">
                            <div className="d-flex align-items-center gap-2 mt-3">
                                <CheckboxItem theme={theme}>
                                    <input
                                        type="radio"
                                        name="isPrimary"
                                        checked={row.isPrimary || false}
                                        onChange={() => handleIsPrimaryChange(index)}
                                    />
                                </CheckboxItem>
                                <FieldLabel theme={theme}>
                                    Mark this organization as primary
                                </FieldLabel>
                                <div className="flex-shrink-0">
                                    <StyledToolTip
                                        placement="top"
                                        title={'Contact Affiliation to Organization'}
                                    >
                                        <TooltipIcon
                                            size={24}
                                            color={theme.CorporateTheme.colors.textSecondary}
                                        />
                                    </StyledToolTip>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const getInputType = (dataType?: string) => {
        switch (dataType) {
            case "email": return "email";
            case "phone": return "tel";
            case "date": return "date";
            case "number": return "number";
            default: return "text";
        }
    };

    const getPlaceholder = (dataType?: string, label?: string) => {
        switch (dataType) {
            case "email": return "email@example.com";
            case "phone": return "+1 (123) 456-7890";
            case "date": return "YYYY-MM-DD";
            default: return `Enter ${label?.toLowerCase()}`;
        }
    };

    const renderMetadataFields = () =>
        metadata.reduce((rows, field, index) => {



            if (field.field === "r.name" && formType === 'contact') {
                return rows;
            }

            if (index % 2 === 0) {
                rows.push(
                    <MetadataRow key={index} $expanded={isFormExpanded && currentPageIndex === 0}>
                        {renderMetadataField(field)}
                        {metadata[index + 1] ? renderMetadataField(metadata[index + 1]) : <div />}
                    </MetadataRow>
                );
            }
            if (formType === 'contact' && field.field === "c.image") {
                rows.push(
                    <OrgRolesCon theme={theme} className="p-2 mb-4" key={`org-${index}`}>
                        {renderMultipleOrganizations()}
                    </OrgRolesCon>
                );
                return rows;
            }
            return rows;
        }, [] as JSX.Element[]);

    const renderFormField = (formField: FormFieldTest) => {
        // Return early if field  is null
        if (!formField?.field) {
            return null;
        }

        const mergedConfig = {
            ...(formField.field?.config || {}),
            ...(formField?.config || {}),
        };

        const normalizedFormField = {
            ...formField,
            config: mergedConfig,
            field: {
                ...formField.field,
                config: mergedConfig,
            },
        };

        const fieldKey = normalizedFormField?.field?.id || 123;
        const value = formFieldValues[fieldKey] ?? "";
        const showError = hasUserInteracted[currentPageIndex] && Boolean(fieldErrors[fieldKey]);
        const dependencyInfo = phoneFieldDependencies.get(fieldKey);
        const linkedCountryValue = dependencyInfo?.countryFieldId ? formFieldValues[dependencyInfo.countryFieldId] : undefined;
        const linkedCountryText = typeof linkedCountryValue === 'string'
            ? linkedCountryValue
            : (linkedCountryValue && typeof linkedCountryValue === 'object')
                ? ("value" in linkedCountryValue && (linkedCountryValue as any).value != null
                    ? String((linkedCountryValue as any).value ?? '')
                    : ("label" in linkedCountryValue && (linkedCountryValue as any).label != null
                        ? String((linkedCountryValue as any).label ?? '')
                        : undefined))
                : undefined;
        const normalizedCallingCode = linkedCountryText ? normalizeCallingCode(linkedCountryText) : null;
        const derivedRegionCode = !normalizedCallingCode && linkedCountryText
            ? getRegionCodeForCountryName(linkedCountryText)
            : null;
        const phoneValidationOptions: PhoneValidationOptions | undefined = dependencyInfo
            ? {
                countryCallingCode: normalizedCallingCode || undefined,
                countryCode: derivedRegionCode || (!normalizedCallingCode ? linkedCountryText : undefined),
                countryFieldLabel: dependencyInfo.countryFieldLabel,
            }
            : undefined;
        return (
            <div className="d-flex align-items-center w-100" style={{ gap: "0.5rem" }}>
                <div className="flex-grow-1">
                    <FormFieldControl
                        key={fieldKey}
                        formField={normalizedFormField}
                        value={value}
                        onChange={makeChangeHandler(normalizedFormField)}
                        onValidationChange={(error: any) => handleFieldError(fieldKey, error)}
                        error={showError ? fieldErrors[fieldKey] : undefined}
                        uploadFolder={formType === 'contact' ? 'contact' : 'organization'}
                        phoneValidationOptions={phoneValidationOptions}
                    />
                </div>
                {normalizedFormField?.field?.config?.uiMeta?.toolTipText && (
                    <div className="flex-shrink-0">
                        <StyledToolTip
                            placement="top"
                            title={normalizedFormField?.field?.config?.uiMeta?.toolTipText || ''}
                        >
                            <TooltipIcon
                                size={24}
                                color={theme.CorporateTheme.colors.textSecondary}
                            />
                        </StyledToolTip>
                    </div>
                )}
            </div>
        );
    };

    const currentPage = formData?.formPages?.[currentPageIndex];
    const isLastPage = currentPageIndex === formData?.formPages?.length - 1;

    const handleSubmit = () => {
        setPageInteracted(currentPageIndex, true);
        if (validateMetadata() && isFormValid()) {
            submitForm();
        }
    };

    const checkReadyToSubmit = (): boolean => {
        if (!formData) return false;

        // 1. Ensure all required form fields have values (presence only)
        const requiredFieldIds: number[] = (formData.formPages || [])
            .flatMap((page: any) =>
                (page.formRows || []).flatMap((row: any) => row.formFields || [])
            )
            .map((f: any) => f?.formField ?? f)
            .filter((ff: any) => ff?.isRequired && ff?.field?.id)
            .map((ff: any) => ff.field.id);

        const allRequiredFilled = requiredFieldIds.every((id: number) => {
            const val = formFieldValues[id];
            if (val === undefined || val === null || val === "") return false;
            if (Array.isArray(val) && val.length === 0) return false;
            return true;
        });

        // 2. Check required metadata presence (don't set errors here)
        const requiredMetadataFilled = metadata.every((m) => {
            if (!m.isRequired) return true;
            // special-case organizationRoles for contacts
            if (m.field === 'o.name' || m.field === 'r.name') {
                if (formType === 'contact') {
                    const rows = (metadataValues.organizationRoles as any[]) || [];
                    // require at least one organization row with organizationId
                    return rows.some(r => r && r.organizationId);
                }
                return true;
            }
            const val = metadataValues[m.field];
            if (val === undefined || val === null || val === '') return false;
            if (Array.isArray(val) && val.length === 0) return false;
            return true;
        });
        return allRequiredFilled && requiredMetadataFilled;
    };

    useEffect(() => {
        setCanSubmit(checkReadyToSubmit());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formFieldValues, metadataValues, metadata, formData]);

    const renderFormFields = () => {
        if (!currentPage) return null;

        return currentPage.formRows.map((row: FormRow) => {
            if (row.layout === 'MULTIPLE' && row.formFields.every(f => f.alignment === 0)) {
                return (
                    <div key={row.id} className="row component-row">
                        <div className="col-12">
                            <div className="component-container p-0 pb-1">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <StyledText fontSize="16px" fontWeight={600} color={theme.CreateFormTheme.colors.primary}>
                                        {row.formFields[0]?.component?.name || row.formFields[0]?.componentName}
                                    </StyledText>
                                </div>
                                <div >
                                    {row.formFields.map((formField: FormFieldTest) => (
                                        <div key={formField.id}>
                                            {renderFormField(formField)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }

            return (
                <PreviewRow key={row.id}>
                    <div className={row.layout === 'SINGLE' ? 'col-12' : 'col-6'}>
                        {row.layout === 'SINGLE'
                            ? row.formFields[0] && renderFormField(row.formFields[0])
                            : row.formFields.find((f: FormFieldTest) => f.alignment === 1) &&
                            renderFormField(row.formFields.find((f: FormFieldTest) => f.alignment === 1)!)
                        }
                    </div>

                    {row.layout === 'MULTIPLE' && (
                        <div className="col-6">
                            {row.formFields.find(f => f.alignment === 2) && (
                                <div className="ms-3">
                                    {renderFormField(row.formFields.find((f: FormFieldTest) => f.alignment === 2)!)}
                                </div>)}
                        </div>
                    )}
                </PreviewRow>
            );
        });
    };

    // Build an index of address field IDs by scope and part (line1, line2, city, state, zip, country)
    const addressFieldIndex = useMemo(() => {
        const index: Record<'home' | 'work' | 'other', Partial<Record<'line1' | 'line2' | 'city' | 'state' | 'zip' | 'country', number>>> = {
            home: {},
            work: {},
            other: {},
        };
        const pages = formData?.formPages || [];
        pages.forEach((p: any) => {
            (p.formRows || []).forEach((r: any) => {
                (r.formFields || []).forEach((ff: any) => {
                    const f = ff?.field;
                    if (!f?.id || !f?.name) return;
                    const name = String(f.name).toLowerCase();
                    const scope: 'home' | 'work' | 'other' | null = name.includes('home')
                        ? 'home'
                        : name.includes('work')
                            ? 'work'
                            : name.includes('other')
                                ? 'other'
                                : null;
                    if (!scope) return;
                    const setIfEmpty = (key: 'line1' | 'line2' | 'city' | 'state' | 'zip' | 'country') => {
                        if (!index[scope][key]) index[scope][key] = f.id;
                    };
                    if (name.includes('line 1') || name.includes('line1')) setIfEmpty('line1');
                    else if (name.includes('line 2') || name.includes('line2')) setIfEmpty('line2');
                    else if (name.includes('city')) setIfEmpty('city');
                    else if (name.includes('state')) setIfEmpty('state');
                    else if (name.includes('zip') || name.includes('postal')) setIfEmpty('zip');
                    else if (name.includes('country')) setIfEmpty('country');
                });
            });
        });
        return index;
    }, [formData]);

    // Helper to extract address parts from Google components
    const deriveAddressParts = useCallback((components: Record<string, string>, formattedAddress: string) => {
        const line1 = [components.street_number, components.route].filter(Boolean).join(' ') || formattedAddress || '';
        const line2 = components.subpremise || '';
        const city = components.locality || components.sublocality || components.administrative_area_level_2 || '';
        const state = components.administrative_area_level_1 || '';
        const zip = components.postal_code || '';
        const country = components.country || '';
        return { line1, line2, city, state, zip, country };
    }, []);

    // Listen for address auto-fill events from field renderers
    useEffect(() => {
        const handler = (e: Event) => {
            const { detail } = e as CustomEvent;
            if (!detail) return;
            let { scope, components, formattedAddress, sourceFieldId } = detail as {
                scope: 'home' | 'work' | 'other' | 'unknown';
                sourceFieldId?: number;
                components: Record<string, string>;
                formattedAddress: string;
            };

            if (!components) return;

            // If scope unknown, infer it from which group contains the source field
            if (scope === 'unknown' && typeof sourceFieldId === 'number') {
                const sourceId = sourceFieldId;
                const matchScope = (['home', 'work', 'other'] as const).find((s) => {
                    const ids = Object.values(addressFieldIndex[s]).filter(
                        (id): id is number => typeof id === 'number'
                    );
                    return ids.includes(sourceId);
                });
                if (matchScope) scope = matchScope;
            }
            if (scope === 'unknown') return;

            const parts = deriveAddressParts(components, formattedAddress);
            const mapping = addressFieldIndex[scope];
            if (!mapping) return;

            setFormFieldValues((prev) => {
                const next = { ...prev };
                if (mapping.line1 && parts.line1) next[mapping.line1] = parts.line1;
                if (mapping.line2 && parts.line2) next[mapping.line2] = parts.line2;
                if (mapping.city && parts.city) next[mapping.city] = parts.city;
                if (mapping.state && parts.state) next[mapping.state] = parts.state;
                if (mapping.zip && parts.zip) next[mapping.zip] = parts.zip;
                if (mapping.country && parts.country) next[mapping.country] = parts.country;
                return next;
            });
        };
        window.addEventListener('AMS_ADDRESS_AUTOFILL', handler as EventListener);
        return () => window.removeEventListener('AMS_ADDRESS_AUTOFILL', handler as EventListener);
    }, [addressFieldIndex, deriveAddressParts]);

    const handleNext = () => {
        if (currentPageIndex < formData.formPages.length - 1) {
            setPageInteracted(currentPageIndex, true);
            const formAndMetaValid = validateMetadata() && isCurrentPageValid();
            if (formAndMetaValid) {
                setCurrentPageIndex(currentPageIndex + 1);
            }
        }
    };

    const handlePrevious = () => {
        setHasUserInteracted(Array(data.data.form.formPages.length).fill(false));
        currentPageIndex > 0 && setCurrentPageIndex(currentPageIndex - 1);
    };

    // Conditional renders
    if (isLoading || isFetchingFormData) return <FormFillerSkeleton />;

    return (
        <ModalOverlay>
            {formData &&
                <ModalContent theme={theme} $expanded={isFormExpanded}
                >
                    <div className="d-flex align-items-center justify-content-between">
                        <FormTitle title={`Add New  ${formType === 'contact' ? 'Contact' : 'Organization'}`} />
                        <div
                            style={{ cursor: 'pointer' }}
                            onClick={onClose}
                        >
                            <CrossIcon />
                        </div>
                    </div>
                    <FormContainer theme={theme} $expandedFirstPage={isFormExpanded && currentPageIndex === 0} $expanded={isFormExpanded}>
                        {isFormExpanded && currentPageIndex === 0 && (
                            <div>
                                <FileUploadWrapper className="px-3 py-3">
                                    <FileUpload
                                        viewImageThumbnail
                                        folder={formType === 'contact' ? 'contact' : 'organization'}
                                        onUploadSuccess={(key) => {
                                            handleMetadataChange(formType === 'contact' ? 'c.image' : 'o.image', key);
                                        }}
                                        onUploadError={(error) => {
                                            showToast("Upload Error", error, "error");
                                        }}
                                    />
                                </FileUploadWrapper>
                                {metadata.length > 0 && (
                                    <div className="px-3 py-3 pb-0">
                                        {renderMetadataFields()}
                                    </div>
                                )}
                            </div>
                        )}
                        {!isFormExpanded && (
                            <div>
                                <FileUploadWrapper className="px-3 py-3">
                                    <FileUpload
                                        viewImageThumbnail
                                        folder={formType === 'contact' ? 'contact' : 'organization'}
                                        onUploadSuccess={(key) => {
                                            handleMetadataChange(formType === 'contact' ? 'c.image' : 'o.image', key);
                                        }}
                                        onUploadError={(error) => {
                                            showToast("Upload Error", error, "error");
                                        }}
                                    />
                                </FileUploadWrapper>
                                {metadata.length > 0 && (
                                    <div className="px-3 py-3 pb-0">
                                        {renderMetadataFields()}
                                    </div>
                                )}
                            </div>
                        )}
                        <FormSection theme={theme} className="px-3 py-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <StyledText fontSize="16px" fontWeight={600} color={theme.CreateFormTheme.colors.primary} >{formData?.name}</StyledText>
                                <div style={{ position: 'relative' }} >
                                    <div className="d-flex">
                                        <Button
                                            theme={theme}
                                            type="button"
                                            $variant="cancel"
                                            className="me-2"
                                            onClick={() => setIsFormExpanded(!isFormExpanded)}
                                        >
                                            {isFormExpanded ? <ContractIcon color={theme.CorporateTheme.colors.textSecondary} /> : <ExpandIcon color={theme.CorporateTheme.colors.textSecondary} />}
                                        </Button>
                                        <AMSSelect
                                            endpoint="/forms"
                                            defaultOptions
                                            additionalParams={formSelectParams}
                                            mapResponse={mapFormsResponse}
                                            value={selectedForm}
                                            onChange={(option) => handleFormSelect(option as FormOption | null)}
                                            placeholder="Switch form"
                                            includeId
                                            isSearchable
                                        />
                                    </div>
                                </div>
                            </div>
                            <PageHeader $expanded={isFormExpanded}>
                                <PageTitle theme={theme}>{currentPage?.title}</PageTitle>
                                {formData?.formPages?.length > 1 &&
                                    <PageIndicator theme={theme}>
                                        Page {currentPageIndex + 1} of {formData.formPages.length}
                                    </PageIndicator>
                                }
                            </PageHeader>
                            {renderFormFields()}
                        </FormSection>
                    </FormContainer>

                    <NavigationButtons />

                    <FormActions>
                        <Button theme={theme} type="button" $variant="cancel" onClick={onClose}>
                            <CloseIcon color={theme.CorporateTheme.colors.textSecondary} /> Cancel
                        </Button>

                        {currentPageIndex > 0 && (
                            <SecondaryButton theme={theme} onClick={handlePrevious}>Previous</SecondaryButton>
                        )}

                        {!isLastPage ? (
                            <PrimaryButton
                                theme={theme}
                                onClick={handleNext}
                            // disabled={isSubmitting || hasUserInteracted[currentPageIndex]}
                            >
                                Next
                            </PrimaryButton>
                        ) : (
                            <Button
                                theme={theme}
                                type="button"
                                $variant="add"
                                onClick={handleSubmit}
                                disabled={!canSubmit || isSubmitting}
                            >
                                {isSubmitting ? <LoadingSpinner size={"18px"} color={theme.CorporateTheme.colors.background} /> :
                                    <AddWhiteIcon color={theme.CorporateTheme.colors.background} />
                                }
                                Add
                                {
                                    formType === 'contact' ? ' Contact' : ' Organization'
                                }
                            </Button>
                        )}
                    </FormActions>
                </ModalContent>
            }
        </ModalOverlay >
    );
};

// Styled components
const FormDropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 300px;
  z-index: 1000;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    padding: 12px;
    background: ${({ theme }) => theme?.CorporateTheme?.colors?.background};
`;

export const FormContainer = styled.div<{ $expandedFirstPage?: boolean; $expanded?: boolean }>`
    max-width: ${({ $expandedFirstPage, $expanded }) => ($expandedFirstPage || $expanded ? 'unset' : '900px')};
    width: 100%;
    border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.border};
    border-radius: 4px;
    display: ${({ $expandedFirstPage }) => ($expandedFirstPage ? 'grid' : 'block')};
    grid-template-columns: ${({ $expandedFirstPage }) => ($expandedFirstPage ? '1fr 3fr' : 'none')};
    column-gap: ${({ $expandedFirstPage }) => ($expandedFirstPage ? CreateFormTheme.spacing.lg : '0')};
`;

const FormSection = styled.div`
  background: ${({ theme }) => theme.CreateFormTheme.colors.addressBG};
  overflow-y: visible;
  overflow: visible;
`;

const PageHeader = styled.div<{ $expanded?: boolean }>`
    margin-bottom: ${({ $expanded }) => ($expanded ? '70px' : '20px')};
    position: relative;
`;

const PageTitle = styled.h2`
  font-size: 16px;
  color: ${({ theme }) => theme.CreateFormTheme.colors.primary};
  margin-bottom: 8px;
  font-weight: 600;
`;

const PageIndicator = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.CreateFormTheme.colors.textSecondary};
  font-weight: 500;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${CreateFormTheme.spacing.lg};
  padding-top: ${CreateFormTheme.spacing.md};
`;

const PrimaryButton = styled(Button)`
  background-color: ${({ theme }) => theme.CreateFormTheme.colors.primaryBtn};
  color: ${({ theme }) => theme.CorporateTheme.colors.background};
  border: none;
  padding: ${CreateFormTheme.spacing.sm} ${CreateFormTheme.spacing.lg};
  font-weight: 600;
  font-size: 14px;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.CreateFormTheme.colors.hoverPrimary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.CreateFormTheme.colors.hoverPrimary};
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
  padding: ${CreateFormTheme.spacing.sm} ${CreateFormTheme.spacing.lg};
  font-weight: 600;
  font-size: 14px;

  &:hover {
    background-color: ${({ theme }) => theme.CreateFormTheme.colors.hoverCancel};
  }
`;

const MetadataRow = styled.div<{ $expanded?: boolean }>`
    display: grid;
    grid-template-columns: ${({ $expanded }) => ($expanded ? '1fr' : 'repeat(2, 1fr)')};
    gap: ${CreateFormTheme.spacing.md};
    width: 100%;
`;

const FileUploadWrapper = styled.div`
  padding: 0 0px 16px;
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: right;
  z-index: 1000;
`;

export const ModalContent = styled.div<{ $expanded?: boolean }>`
    background: ${({ theme }) => theme.CreateFormTheme.colors.background};
    padding: 0 ${CreateFormTheme.spacing.lg} ${CreateFormTheme.spacing.lg};
    width: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;

    /* default for larger monitors */
    max-width: ${({ $expanded }) => ($expanded ? '70vw' : '40vw')};

    /* laptops (adjust breakpoint as needed) */
    @media (max-width: 1440px) {
        max-width: ${({ $expanded }) => ($expanded ? '75vw' : '45vw')};
    }
`;


const PreviewRow = styled.div`
  display: flex;
`;

const StyledText = styled.span<{ fontSize?: string; fontWeight?: number; color?: string }>`
  font-size: ${props => props.fontSize || '14px'};
  font-weight: ${props => props.fontWeight || 400};
  color: ${props => props.color || CreateFormTheme.colors.textPrimary};
  font-family: 'Raleway', sans-serif;
`;

export const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${CreateFormTheme.spacing.md};
    margin-top: auto;
    padding-top: ${CreateFormTheme.spacing.md};
`;

const FieldLabel = styled(StyledText)`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  text-transform: capitalize;
`;
const ErrorText = styled.span`
  color: ${({ theme }) => theme.CreateFormTheme.colors.error};
  font-size: 12px;
  margin-top: 4px;
  display: block;
`;
const OrgRolesCon = styled.div`
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    border-style: dashed;
    border-radius: 5px;
    background: ${({ theme }) => theme.CreateFormTheme.colors.addressBG};
    & .row {
        padding: 20px 0px;
    }
    & .row:not(:last-child) {
        border-bottom: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    }
`;
export default FormFiller;
